import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { FormModal } from '@/components/ui/form-modal';
import { Button } from '@/components/ui/button';
import { MultiIdCombobox } from '@/components/ui/multi-id-combobox';
import config from 'constants/apiConfig';
import { changeGroupAdminNameToOrgAdmin } from 'components/Roles/RolesUtils/groupToOrgTransformations';
import type { LayerGrant } from 'hooks/internalLayer/types';

type SaveDraftPermissionsDialogProps = {
  open: boolean;
  groupGuid: string;
  /** Incident workspace limits the list to that incident's roles. */
  scope: 'incident' | 'hub';
  incidentId: string | null;
  pending: boolean;
  initialGrants?: LayerGrant[];
  onCancel: () => void;
  onConfirm: (grants: LayerGrant[]) => void;
};

function RolePermissionFields({
  options,
  initialViewIds,
  initialEditIds,
  emptyMessage,
  pending,
  onCancel,
  onConfirm,
}: {
  options: { value: string; label: string }[];
  initialViewIds: string[];
  initialEditIds: string[];
  emptyMessage: string;
  pending: boolean;
  onCancel: () => void;
  onConfirm: (grants: LayerGrant[]) => void;
}) {
  const [viewIds, setViewIds] = useState(initialViewIds);
  const [editIds, setEditIds] = useState(initialEditIds);

  const setView = (next: string[]) => {
    setViewIds(next);
    setEditIds((current) => current.filter((id) => next.includes(id)));
  };

  const setEdit = (next: string[]) => {
    setEditIds(next);
    setViewIds((current) => Array.from(new Set([...current, ...next])));
  };

  const confirm = () => {
    const grants: LayerGrant[] = viewIds.map((id) => ({
      subjectType: 'role',
      subjectId: id,
      canView: true,
      canEdit: editIds.includes(id),
    }));
    onConfirm(grants);
  };

  return (
    <>
      <div className="tw:flex tw:flex-col tw:gap-4">
        {options.length === 0 ? (
          <p className="tw:mb-0 tw:text-sm tw:text-muted-foreground">
            {emptyMessage}
          </p>
        ) : null}
        <div className="tw:flex tw:flex-col tw:gap-1.5">
          <div className="tw:text-sm">View layer</div>
          <MultiIdCombobox
            options={options}
            value={viewIds}
            onChange={setView}
            placeholder="Search roles"
            disabled={pending || options.length === 0}
          />
        </div>
        <div className="tw:flex tw:flex-col tw:gap-1.5">
          <div className="tw:text-sm">Edit layer</div>
          <MultiIdCombobox
            options={options}
            value={editIds}
            onChange={setEdit}
            placeholder="Search roles"
            disabled={pending || options.length === 0}
          />
        </div>
      </div>
      <div className="tw:sticky tw:bottom-0 tw:flex tw:justify-end tw:gap-2 tw:bg-background tw:pt-3">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={pending}
        >
          Cancel
        </Button>
        <Button type="button" onClick={confirm} disabled={pending}>
          {pending ? 'Saving…' : 'Save Layer Permissions'}
        </Button>
      </div>
    </>
  );
}

export function SaveDraftPermissionsDialog({
  open,
  groupGuid,
  scope,
  incidentId,
  pending,
  initialGrants = [],
  onCancel,
  onConfirm,
}: SaveDraftPermissionsDialogProps) {
  const inIncident = scope === 'incident';
  const { data: roles = [], isFetched } = useQuery({
    queryKey: ['layer-draft-roles', groupGuid, scope, incidentId],
    enabled: open && Boolean(groupGuid) && (!inIncident || Boolean(incidentId)),
    queryFn: async () => {
      const response = await axios.post<{ id: string; name: string }[]>(
        config.apiGateway.fetchRolesForLayerDraft,
        {
          // eslint-disable-next-line camelcase -- matches the roles API body
          group_guid: groupGuid,
          // eslint-disable-next-line camelcase -- matches the roles API body
          incident_id: incidentId,
        }
      );
      return Array.isArray(response.data) ? response.data : [];
    },
  });

  const options = roles.map((role) => ({
    value: String(role.id),
    label: inIncident ? role.name : changeGroupAdminNameToOrgAdmin(role.name),
  }));
  const roleGrants = initialGrants.filter(
    (grant) => grant.subjectType === 'role' && !grant.scopeType
  );
  const initialViewIds = roleGrants
    .filter((grant) => grant.canView || grant.canEdit)
    .map((grant) => grant.subjectId);
  const initialEditIds = roleGrants
    .filter((grant) => grant.canEdit)
    .map((grant) => grant.subjectId);

  const description = inIncident
    ? 'You will always be able to view and edit this layer. Choose which incident roles can also view it and which can edit it. Select no roles to leave the layer private.'
    : 'You will always be able to view and edit this layer. Choose which organization and area of responsibility roles can also view it and which can edit it. Select no roles to leave the layer private.';

  return (
    <FormModal
      open={open}
      onOpenChange={(next) => {
        if (!next && !pending) onCancel();
      }}
      title="Confirm Layer Permissions"
      description={description}
    >
      {inIncident && !incidentId ? (
        <p className="tw:mb-0 tw:text-sm tw:text-muted-foreground">
          This incident has no roles to assign.
        </p>
      ) : !isFetched ? (
        <p className="tw:mb-0 tw:text-sm tw:text-muted-foreground">
          Loading roles…
        </p>
      ) : (
        <RolePermissionFields
          key={`${incidentId ?? 'hub'}:${options.map((option) => option.value).join('|')}:${initialViewIds.join('|')}:${initialEditIds.join('|')}`}
          options={options}
          initialViewIds={initialViewIds}
          initialEditIds={initialEditIds}
          emptyMessage={
            inIncident
              ? 'This incident has no roles to assign.'
              : 'This organization has no roles to assign.'
          }
          pending={pending}
          onCancel={onCancel}
          onConfirm={onConfirm}
        />
      )}
    </FormModal>
  );
}
